import type { Sequence } from '@/types'
import {
  DEFAULT_RENDER_TARGET_FRAME,
  isValidRenderTargetFrame,
  resolveSequenceRenderFrame,
  type RenderFit,
  type RenderTargetFrame,
} from '@/types/shared'

export { resolveSequenceRenderFrame }

export type SequenceFramePreset = '16:9' | '9:16' | '1:1' | '4:5' | '21:9' | '4:3' | '3:4'

export interface SequenceFramePresetDefinition {
  label: string
  description: string
  width: number
  height: number
}

export const SEQUENCE_FRAME_PRESETS = {
  '16:9': { label: '16:9', description: 'Landscape', width: 1920, height: 1080 },
  '9:16': { label: '9:16', description: 'Vertical', width: 1080, height: 1920 },
  '1:1': { label: '1:1', description: 'Square', width: 1080, height: 1080 },
  '4:5': { label: '4:5', description: 'Portrait', width: 1080, height: 1350 },
  '21:9': { label: '21:9', description: 'Widescreen', width: 2560, height: 1080 },
  '4:3': { label: '4:3', description: 'Classic', width: 1440, height: 1080 },
  '3:4': { label: '3:4', description: 'Classic Portrait', width: 1080, height: 1440 },
} as const satisfies Record<SequenceFramePreset, SequenceFramePresetDefinition>

export const SEQUENCE_FRAME_PRESET_ORDER: readonly SequenceFramePreset[] = [
  '16:9',
  '9:16',
  '1:1',
  '4:5',
  '21:9',
  '4:3',
  '3:4',
]

export const DEFAULT_SEQUENCE_FRAME = DEFAULT_RENDER_TARGET_FRAME

/** Resolve legacy or malformed collaborative sequence data to a safe 16:9 frame. */
export function resolveSequenceFrame(sequence: Pick<Sequence, 'frame'> | null | undefined): RenderTargetFrame {
  const frame = sequence?.frame
  return isValidRenderTargetFrame(frame) ? { ...frame } : { ...DEFAULT_SEQUENCE_FRAME }
}

export function createSequenceFrame(preset: SequenceFramePreset, fit: RenderFit): RenderTargetFrame {
  const { width, height } = SEQUENCE_FRAME_PRESETS[preset]
  return { width, height, fit }
}

export function findSequenceFramePreset(frame: RenderTargetFrame): SequenceFramePreset | null {
  return (
    SEQUENCE_FRAME_PRESET_ORDER.find(preset => {
      const definition = SEQUENCE_FRAME_PRESETS[preset]
      return definition.width * frame.height === definition.height * frame.width
    }) ?? null
  )
}

/** Match canonical preset dimensions without collapsing a custom frame of the same aspect ratio. */
export function findExactSequenceFramePreset(frame: RenderTargetFrame): SequenceFramePreset | null {
  return (
    SEQUENCE_FRAME_PRESET_ORDER.find(preset => {
      const definition = SEQUENCE_FRAME_PRESETS[preset]
      return definition.width === frame.width && definition.height === frame.height
    }) ?? null
  )
}

export interface FrameDrawRect {
  x: number
  y: number
  width: number
  height: number
}

export interface SourceDimensions {
  width: number
  height: number
}

export interface CanvasBackingSize extends SourceDimensions {
  scale: number
}

/** Cap monitor backing stores independently from delivery resolution to avoid GPU-memory spikes. */
export function resolveCanvasBackingSize(
  logicalWidth: number,
  logicalHeight: number,
  devicePixelRatio: number,
  maxDimension = 2048,
): CanvasBackingSize {
  const dimensions = resolveSourceDimensions(logicalWidth, logicalHeight, { width: 1, height: 1 })
  const dpr = Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? Math.min(devicePixelRatio, 2) : 1
  const scale = Math.min(dpr, maxDimension / Math.max(dimensions.width, dimensions.height))
  return {
    width: Math.max(1, Math.round(dimensions.width * scale)),
    height: Math.max(1, Math.round(dimensions.height * scale)),
    scale,
  }
}

/** Prefer container metadata, falling back to a successfully decoded frame. */
export function resolveSourceDimensions(width: number, height: number, fallback: SourceDimensions): SourceDimensions {
  const resolvedWidth = Number.isFinite(width) && width > 0 ? width : fallback.width
  const resolvedHeight = Number.isFinite(height) && height > 0 ? height : fallback.height

  if (![resolvedWidth, resolvedHeight].every(value => Number.isFinite(value) && value > 0)) {
    throw new Error('Source dimensions must be positive finite numbers')
  }

  return { width: resolvedWidth, height: resolvedHeight }
}

/** Fit a frame inside a UI viewport without changing its aspect ratio. */
export function fitFrameWithinBounds(
  frameWidth: number,
  frameHeight: number,
  boundsWidth: number,
  boundsHeight: number,
): SourceDimensions {
  const frame = resolveSourceDimensions(frameWidth, frameHeight, { width: 1, height: 1 })
  const bounds = resolveSourceDimensions(boundsWidth, boundsHeight, { width: 1, height: 1 })
  const frameRatio = frame.width / frame.height

  if (bounds.width / bounds.height > frameRatio) {
    return { width: bounds.height * frameRatio, height: bounds.height }
  }

  return { width: bounds.width, height: bounds.width / frameRatio }
}

/** Calculate centered, aspect-preserving canvas geometry for sequence Fit or Fill. */
export function fitSourceToFrame(
  sourceWidth: number,
  sourceHeight: number,
  targetFrame: RenderTargetFrame,
): FrameDrawRect {
  if (![sourceWidth, sourceHeight].every(value => Number.isFinite(value) && value > 0)) {
    throw new Error('Source dimensions must be positive finite numbers')
  }

  const scale =
    targetFrame.fit === 'contain'
      ? Math.min(targetFrame.width / sourceWidth, targetFrame.height / sourceHeight)
      : Math.max(targetFrame.width / sourceWidth, targetFrame.height / sourceHeight)
  const width = sourceWidth * scale
  const height = sourceHeight * scale

  return {
    x: (targetFrame.width - width) / 2,
    y: (targetFrame.height - height) / 2,
    width,
    height,
  }
}
